import { createSelector } from '@reduxjs/toolkit';

import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import type { DecoratedDeliveryOption } from './types';
import { getMinMaxDeliveryEstimate } from './utils/getMinMaxDeliveryEstimate';

export const selectSelectedZone = ({ delivery }: ReduxApplicationState) => delivery.selectedZone;

export const selectSelectedCountry = ({ delivery }: ReduxApplicationState) =>
  delivery.selectedCountry;

export const selectDeliveryOptions = ({ delivery }: ReduxApplicationState) => delivery.options;

export const selectDeliveryOptionsForZone = createSelector(
  [selectSelectedZone, selectDeliveryOptions],
  (selectedZone, options) =>
    options.find(({ zoneCode }) => zoneCode === selectedZone)?.deliveryOptions,
);

const selectUserCurrencyCode = (state: ReduxApplicationState) => {
  return state.user?.currencyCode;
};

export const selectDecoratedDeliveryOptions = createSelector(
  [selectDeliveryOptionsForZone, selectUserCurrencyCode],
  (deliveryOptionsForZone, userCurrency = 'GBP'): DecoratedDeliveryOption[] | [] => {
    const deliveryOptions =
      deliveryOptionsForZone?.map(
        ({
          cutoffDateTime,
          date,
          deliveryProvider,
          exactDate,
          freePartnerShipping,
          max,
          min,
          optionCode,
          prices,
        }) => ({
          cutoffDateTime,
          date,
          exactDate,
          max,
          min,
          optionCode,
          partnerFreeDeliveryThresholdPrice:
            freePartnerShipping?.thresholdPrices?.find(
              ({ currencyCode }) => currencyCode === userCurrency,
            ) ?? null,
          price: prices.find(({ currencyCode }) => currencyCode === userCurrency)!,
          providerName: deliveryProvider?.name,
        }),
      ) ?? [];

    return deliveryOptions;
  },
);

export const selectMinMaxDeliveryEstimates = createSelector(
  [selectDecoratedDeliveryOptions],
  (countryDeliveryOptions) => getMinMaxDeliveryEstimate(countryDeliveryOptions),
);
